// src/api/agentAwareApiAdapter.ts

import { chatApi } from "./chatApi";
import { executedContractsApi } from "./executedContractsApi";
import { draftingAssistantApi } from "./draftingAssistantApi";
import { generalAgentApi } from "./generalAgentApi";
import { fetchContractSummaryStream } from "./contractSummaryApi";
import {
  UnifiedChatRequest,
  UnifiedSummaryRequest,
  generateAgentSessionId,
} from "../interfaces/agentApiTypes";
import { ChatStreamCallbacks } from "../interfaces/chatTypes";
import { createLogger } from "../utils/logger";
import { normalizeAccessToken } from "../utils/typeHelpers";

const log = createLogger("AgentAwareApiAdapter");

/**
 * Agent-Aware API Adapter
 *
 * This adapter acts as an intelligent router that directs API requests
 * to the appropriate backend based on the current agent.
 *
 * Think of it as a smart postal service that knows which post office
 * to send your package to based on the destination (agent).
 *
 * Key responsibilities:
 * 1. Route requests to the correct API endpoint
 * 2. Transform unified requests into agent-specific formats
 * 3. Manage agent-specific session IDs
 * 4. Handle additional files for contract-qa
 */
export class AgentAwareApiAdapter {
  // Track session IDs per agent
  private agentSessions: Map<string, string> = new Map();

  /**
   * Send a unified chat request to the appropriate agent API
   *
   * This method takes a common request format and transforms it
   * into the specific format required by each agent's API.
   */
  async sendChatRequest(
    request: UnifiedChatRequest,
    agentId: string,
    callbacks: ChatStreamCallbacks
  ): Promise<void> {
    log.info("Routing chat request", {
      agentId,
      hasAdditionalFiles: !!(request.additionalFiles && request.additionalFiles.length > 0),
      messagePreview: request.message.substring(0, 50) + "...",
    });

    // TEMPORARY DEBUG: Show routing decision
    const routingInfo = {
      agent: agentId,
      willRouteTo:
        agentId === "contract-qa"
          ? "EXECUTED_CONTRACTS_API"
          : agentId === "drafting-assistant"
            ? "DRAFTING_ASSISTANT_API"
            : "REDLINE_ANALYZER_API",
      endpoint:
        agentId === "contract-qa"
          ? "/chat/executed_contracts/stream/"
          : agentId === "drafting-assistant"
            ? "/chat/document_generator/stream/"
            : "/chat/redline_analyzer/stream/",
      hasAdditionalFiles: request.additionalFiles?.length || 0,
    };
    log.debug("🚦 ROUTING DECISION - CHAT:", routingInfo);
    log.info("🎯 CHAT ROUTING:", routingInfo);

    // Get or create session ID for this agent
    const sessionId = this.getOrCreateAgentSession(agentId, request.sessionId);

    switch (agentId) {
      case "ccm-space":
        return this.sendCCMSpaceChatRequest(request, sessionId, callbacks);

      case "contract-qa":
        return this.sendContractQAChatRequest(request, sessionId, callbacks);

      case "drafting-assistant":
        return this.sendDraftingAssistantChatRequest(request, sessionId, callbacks);

      case "red-line-analyzer":
      default:
        return this.sendRedlineAnalyzerChatRequest(request, sessionId, callbacks);
    }
  }

  /**
   * Send a unified summary request to the appropriate agent API
   *
   * @deprecated RETIRED along with the Analysis tab. Its only caller was
   * AgentAwareModelService.generateStreamingSummary, which is itself
   * unreachable, and the `/summary/*` routes below are unmounted on the
   * backend. Kept for a possible revival, which needs those routes back.
   */
  async sendSummaryRequest(
    request: UnifiedSummaryRequest,
    agentId: string,
    callbacks: {
      onStart: () => void;
      onChunk: (chunk: string) => void;
      onComplete: () => void;
      onError: (error: Error) => void;
    }
  ): Promise<AbortController> {
    log.info("Routing summary request", {
      agentId,
      hasAdditionalFiles: !!(request.additionalFiles && request.additionalFiles.length > 0),
    });

    // TEMPORARY DEBUG: Show summary routing decision
    log.debug("🚦 ROUTING DECISION - SUMMARY:", {
      agent: agentId,
      willRouteTo:
        agentId === "contract-qa"
          ? "EXECUTED_CONTRACTS_SUMMARY_API"
          : "REDLINE_ANALYZER_SUMMARY_API",
      endpoint:
        agentId === "contract-qa"
          ? "/summary/executed_contracts/stream/"
          : "/summary/redline_analyzer/stream/",
    });

    // Get or create session ID for this agent
    const sessionId = this.getOrCreateAgentSession(agentId, request.sessionId);

    switch (agentId) {
      case "ccm-space":
        // CCM Space doesn't support summary
        log.warn("Summary request attempted for ccm-space, which doesn't support summaries");
        callbacks.onError(new Error("Summary not supported for CCM Space"));
        return new AbortController();

      case "contract-qa":
        return this.sendContractQASummaryRequest(request, sessionId, callbacks);

      case "drafting-assistant":
        // Drafting assistant doesn't support summary
        log.warn(
          "Summary request attempted for drafting-assistant, which doesn't support summaries"
        );
        callbacks.onError(new Error("Summary not supported for Drafting Assistant"));
        return new AbortController();

      case "red-line-analyzer":
      default:
        return this.sendRedlineAnalyzerSummaryRequest(request, sessionId, callbacks);
    }
  }

  /**
   * Cancel any ongoing requests
   */
  cancelRequest(agentId: string): void {
    switch (agentId) {
      case "ccm-space":
        generalAgentApi.cancelRequest();
        break;

      case "contract-qa":
        executedContractsApi.cancelRequest();
        break;

      case "drafting-assistant":
        draftingAssistantApi.cancelRequest();
        break;

      case "red-line-analyzer":
      default:
        chatApi.cancelRequest();
        break;
    }
  }

  /**
   * Clear session for a specific agent
   *
   * This is important when switching agents to ensure clean conversation boundaries.
   */
  clearAgentSession(agentId: string): void {
    log.info("Clearing session for agent", { agentId });
    this.agentSessions.delete(agentId);
  }

  /**
   * Adopt a backend session ID that came back from a stored conversation
   *
   * Without this the map stays empty until the agent's first request, so a
   * session saved between the restore and that request would report no session
   * ID and overwrite the stored one with null, splitting the conversation in
   * the usage reports.
   *
   * @param agentId The agent the session belongs to
   * @param sessionId The stored session ID; null is ignored
   */
  adoptAgentSession(agentId: string, sessionId: string | null): void {
    if (!sessionId) return;

    log.info("Adopting stored session for agent", { agentId, sessionId });
    this.agentSessions.set(agentId, sessionId);
  }

  /**
   * Get the backend session ID currently in use for an agent
   *
   * This map is the authoritative record of the `id` sent to the backend, and
   * it is what has to be stored alongside a persisted conversation so a reopen
   * continues the same session rather than opening a new one.
   *
   * @returns The session ID, or null if the agent has not sent a request yet
   */
  getAgentSessionId(agentId: string): string | null {
    return this.agentSessions.get(agentId) ?? null;
  }

  /**
   * Clear all agent sessions
   */
  clearAllSessions(): void {
    log.info("Clearing all agent sessions", {
      agentCount: this.agentSessions.size,
    });
    this.agentSessions.clear();
  }

  /**
   * Get or create a session ID for a specific agent
   */
  private getOrCreateAgentSession(agentId: string, providedSessionId?: string): string {
    // If a session ID was provided, use it and store it
    if (providedSessionId) {
      this.agentSessions.set(agentId, providedSessionId);
      return providedSessionId;
    }

    // Check if we have an existing session for this agent
    let sessionId = this.agentSessions.get(agentId);

    if (!sessionId) {
      // Create a new agent-specific session ID
      sessionId = generateAgentSessionId(agentId);
      this.agentSessions.set(agentId, sessionId);

      log.info("Created new session for agent", {
        agentId,
        sessionId,
      });
    }

    return sessionId;
  }

  /**
   * Send chat request to red-line-analyzer API
   */
  private async sendRedlineAnalyzerChatRequest(
    request: UnifiedChatRequest,
    sessionId: string,
    callbacks: ChatStreamCallbacks
  ): Promise<void> {
    // Transform unified request to red-line-analyzer format
    const apiRequest = {
      message: request.message,
      full_file: request.documentFile, // Note the different parameter name
      additional_files: request.additionalFiles, // Pass additional files array
      selected_text_file: request.selectedTextFile,
      id: sessionId,
      doc_id: request.documentId,
      bypass_cache: request.bypassCache,
      details: request.details,
      chat_history: request.chatHistory,
      access_token: request.accessToken, // Optional access token
    };

    log.debug("Sending red-line-analyzer chat request", {
      sessionId,
      hasSelectedText: !!request.selectedTextFile,
      additionalFileCount: request.additionalFiles?.length || 0,
    });

    await chatApi.sendStreamingChatRequest(apiRequest, callbacks);
  }

  /**
   * Send chat request to contract-qa API
   */
  private async sendContractQAChatRequest(
    request: UnifiedChatRequest,
    sessionId: string,
    callbacks: ChatStreamCallbacks
  ): Promise<void> {
    // Transform unified request to contract-qa format
    const apiRequest = {
      message: request.message,
      executedAgreementFile: request.documentFile, // Different parameter name
      additionalFiles: request.additionalFiles, // Pass all template files
      selectedTextFile: request.selectedTextFile,
      id: sessionId,
      docId: request.documentId,
      bypassCache: request.bypassCache,
      details: request.details,
      chatHistory: request.chatHistory,
      accessToken: request.accessToken, // Optional access token
    };

    log.debug("Sending contract-qa chat request", {
      sessionId,
      additionalFileCount: request.additionalFiles?.length || 0,
      hasSelectedText: !!request.selectedTextFile,
    });

    await executedContractsApi.sendStreamingChatRequest(apiRequest, callbacks);
  }

  /**
   * Send summary request to red-line-analyzer API
   *
   * @deprecated RETIRED - reachable only from the deprecated sendSummaryRequest.
   */
  private async sendRedlineAnalyzerSummaryRequest(
    request: UnifiedSummaryRequest,
    sessionId: string,
    callbacks: any
  ): Promise<AbortController> {
    const params = {
      id: sessionId,
      bypass_cache: request.bypassCache,
      details: request.details,
    };

    // TEMPORARY DEBUG: Log summary routing
    log.debug("📊 ROUTING SUMMARY TO RED-LINE ANALYZER:", {
      sessionId,
      endpoint: "/summary/redline_analyzer/stream/",
    });

    log.debug("Sending red-line-analyzer summary request", {
      sessionId,
    });

    // Note: fetchContractSummaryStream expects 'file' as the parameter
    return fetchContractSummaryStream(
      request.documentFile,
      params,
      callbacks,
      normalizeAccessToken(request.accessToken) || ""
    );
  }
  /**
   * Send summary request to contract-qa API
   *
   * @deprecated RETIRED - reachable only from the deprecated sendSummaryRequest.
   */
  private async sendContractQASummaryRequest(
    request: UnifiedSummaryRequest,
    sessionId: string,
    callbacks: any
  ): Promise<AbortController> {
    const apiRequest = {
      executedAgreementFile: request.documentFile,
      additionalFiles: request.additionalFiles,
      id: sessionId,
      bypassCache: request.bypassCache,
      details: request.details,
      docId: request.documentId,
      accessToken: request.accessToken, // Optional access token
    };

    log.debug("Sending contract-qa summary request", {
      sessionId,
      additionalFileCount: request.additionalFiles?.length || 0,
    });

    return await executedContractsApi.sendStreamingSummaryRequest(apiRequest, callbacks);
  }

  /**
   * Send chat request to drafting-assistant API
   */
  private async sendDraftingAssistantChatRequest(
    request: UnifiedChatRequest,
    sessionId: string,
    callbacks: ChatStreamCallbacks
  ): Promise<void> {
    // Transform unified request to drafting-assistant format
    const apiRequest = {
      message: request.message,
      full_text_file: request.documentFile,
      additional_context: request.additionalFiles, // Pass all files as array
      selected_text: request.selectedTextFile,
      id: sessionId,
      bypass_cache: request.bypassCache,
      details: request.details,
      chat_history: request.chatHistory,
      access_token: request.accessToken, // Optional access token
    };

    log.debug("Sending drafting-assistant chat request", {
      sessionId,
      contextFileCount: request.additionalFiles?.length || 0,
      hasSelectedText: !!request.selectedTextFile,
    });

    log.info("🔄 ADAPTER -> DRAFTING API:", {
      sessionId,
      additionalFiles: apiRequest.additional_context?.map((f) => f.name) || [],
      documentFile: apiRequest.full_text_file.name,
    });

    await draftingAssistantApi.sendStreamingChatRequest(apiRequest, callbacks);
  }

  /**
   * Send chat request to CCM Space API
   */
  private async sendCCMSpaceChatRequest(
    request: UnifiedChatRequest,
    sessionId: string,
    callbacks: ChatStreamCallbacks
  ): Promise<void> {
    // Transform unified request to CCM Space format (similar to drafting assistant)
    const apiRequest = {
      message: request.message,
      full_text_file: request.documentFile,
      additional_context: request.additionalFiles, // Pass all files as array
      selected_text: request.selectedTextFile,
      id: sessionId,
      bypass_cache: request.bypassCache,
      details: request.details,
      chat_history: request.chatHistory,
      access_token: request.accessToken, // Optional access token
    };

    log.debug("Sending CCM Space chat request", {
      sessionId,
      contextFileCount: request.additionalFiles?.length || 0,
      hasSelectedText: !!request.selectedTextFile,
    });

    log.info("🔄 ADAPTER -> CCM SPACE API:", {
      sessionId,
      additionalFiles: apiRequest.additional_context?.map((f) => f.name) || [],
      documentFile: apiRequest.full_text_file.name,
    });

    await generalAgentApi.sendStreamingChatRequest(apiRequest, callbacks);
  }
}

// Export singleton instance
export const agentAwareApiAdapter = new AgentAwareApiAdapter();
